import { Dispatch, SetStateAction, useEffect, useState } from "react";
import styles from "../BundleBuilder.module.css";
import {
    Banner,
    BlockStack,
    Button,
    Card,
    Checkbox,
    Collapsible,
    Divider,
    DropZone,
    Icon,
    Label,
    LegacyCard,
    Tabs,
    Text,
    Tooltip,
} from "@shopify/polaris";
import {
    DeleteIcon,
    DragHandleIcon,
    InfoIcon,
    PlusCircleIcon,
    DuplicateIcon,
    SettingsFilledIcon,
    SandboxIcon,
    GiftCardIcon,
    FileIcon,
    ListBulletedIcon,
    AppsIcon,
    XIcon,
} from "@shopify/polaris-icons";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import RHFRenderSelectedProduct from "../../../../components/RHFRenderSelectedProduct/RHFRenderSelectedProduct";
import { BundleTypes, nonEmptyOverride } from "../../constants";
import { ProductObj, StepsType, SubStepType } from "../../Types";
import RHFRenderSelectedCollections from "../../../../components/RHFRenderSelectedCollections/RHFRenderSelectedCollections";
import BBSubStep, { BBSubStepConditions } from "./BBSubStep";
import ImageUploader from "../../../../components/ImageUploader/ImageUploader";
import { DEFAULT_SUB_STEP } from "../hooks/useBundleBuilderHook";
import ShopifyNativeError from "../../../../components/ShopifyNativeError/ShopifyNativeError";
import { getConditionMessage } from "../../../../utilities/constants";
import StepConditions from "../../../../components/StepConditions/StepConditions";
import {
    buildResourcePickerQuery,
    isDispensaryCoStore,
    isLilSweetTreatStore,
    MAYUR_TEST_STORE,
} from "../../../constants";
import MonacoEditor from "@monaco-editor/react";
import { useFormContext } from "react-hook-form";
import { FormCheckbox, FormRadioButton, FormSelect, FormTextField } from "../../../../components/FormFields";

export const FieldRow = ({
    handleEditFields,
    field,
    sIdx,
    handleAddFields,
    pIdx,
    cartAddon = false,
    hasInteracted,
    setHasInteracted,
}: {
    handleEditFields: any;
    field: {
        field_type: "input" | "textarea" | "dropdown" | "file_upload";
        field_title: string;
        field_placeholder: string;
        is_required: boolean;
        dropdown_options?: string[];
        default_value?: string;
        file_upload_settings?: {
            max_file_size: number;
            allow_multiple: boolean;
            allowed_file_types: ("images" | "documents")[];
        };
    };
    sIdx: number;
    handleAddFields: any;
    pIdx: number;
    cartAddon?: boolean;
    hasInteracted?: boolean;
    setHasInteracted: (val: boolean) => void;
}) => {
    const [fieldError, setFieldError] = useState<{ field_title: string }>({ field_title: "" });

    const dropdownError =
        hasInteracted &&
        field.field_type === "dropdown" &&
        (!field.dropdown_options || field.dropdown_options.length === 0)
            ? "At least one option is required for dropdown fields"
            : "";

    const fileUploadError =
        hasInteracted &&
        field.field_type === "file_upload" &&
        (!field.file_upload_settings?.allowed_file_types ||
            field.file_upload_settings?.allowed_file_types?.length === 0)
            ? "At least one file type must be selected"
            : "";

    useEffect(() => {
        if (hasInteracted) {
            setFieldError({ field_title: nonEmptyOverride(field?.field_title, "Field Title is required") });
        } else {
            setFieldError({ field_title: "" });
        }
    }, [field?.field_title, hasInteracted]);

    return (
        <Card>
            <div
                className={styles.customCardLabelWrapper}
                style={{ marginBottom: "5px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
                <Label id="step_condition">Custom Field {sIdx + 1}</Label>
                <span
                    className={styles.stepDragIcon}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleAddFields(pIdx, "remove", sIdx);
                    }}
                >
                    <Icon source={DeleteIcon} tone="critical" />
                </span>
            </div>
            <div className={`${styles.formRow}`}>
                <FormSelect
                    name={`steps.${pIdx}.fields.${sIdx}.field_type`}
                    label={`Field Type ${sIdx + 1}`}
                    options={[
                        { label: "TextField", value: "input" },
                        { label: "Textarea", value: "textarea" },
                        { label: "Dropdown", value: "dropdown" },
                        { label: "File Upload", value: "file_upload" },
                    ]}
                    onChange={(val) => handleEditFields(pIdx, sIdx, "field_type", val)}
                    value={field.field_type}
                />
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", marginBottom: -20 }}>
                    <FormCheckbox
                        name={`steps.${pIdx}.fields.${sIdx}.is_required`}
                        label="Make this Field Required"
                        checked={field.is_required}
                        onChange={(val) => handleEditFields(pIdx, sIdx, "is_required", val)}
                    />
                </div>

                <div style={{ gridColumn: cartAddon ? "1 / -1" : "auto" }}>
                    <FormTextField
                        name={`steps.${pIdx}.fields.${sIdx}.field_title`}
                        label="Field Title"
                        placeholder="Field Title"
                        value={field.field_title}
                        onBlur={() => {
                            setFieldError({
                                field_title: nonEmptyOverride(field.field_title, "Field Title is required"),
                            });
                            if (!hasInteracted) setHasInteracted(true);
                        }}
                        onChange={(val) => {
                            setFieldError({ field_title: nonEmptyOverride(val, "Field Title is required") });
                            if (!hasInteracted) setHasInteracted(true);
                            handleEditFields(pIdx, sIdx, "field_title", val);
                        }}
                        maxLength={cartAddon ? 100 : 40}
                        showCharacterCount
                        error={fieldError?.field_title}
                    />
                </div>
                {field.field_type !== "file_upload" && (
                    <div style={{ gridColumn: cartAddon ? "1 / -1" : "auto" }}>
                        <FormTextField
                            name={`steps.${pIdx}.fields.${sIdx}.field_placeholder`}
                            label="Placeholder Text"
                            placeholder="Placeholder Text"
                            value={field.field_placeholder}
                            onChange={(val) => {
                                if (!hasInteracted) setHasInteracted(true);
                                handleEditFields(pIdx, sIdx, "field_placeholder", val);
                            }}
                            maxLength={cartAddon ? 100 : 40}
                            showCharacterCount
                        />
                    </div>
                )}
            </div>
            {field.field_type === "dropdown" && (
                <div style={{ marginTop: "16px" }}>
                    <BlockStack gap="400">
                        <Text as="h3" fontWeight="medium">
                            Dropdown Options
                        </Text>
                        <div
                            style={{
                                border: "1px solid #e1e1e1",
                                borderRadius: "8px",
                                padding: "12px",
                                backgroundColor: "#ffffff",
                            }}
                        >
                            <BlockStack gap="300">
                                {(field.dropdown_options || []).map((option, oIdx) => {
                                    const optionValue =
                                        typeof option === "string"
                                            ? option
                                            : (option as any)?.value || (option as any)?.label || "";
                                    return (
                                        <div key={oIdx} style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                            <div style={{ marginTop: "2px" }}>
                                                <Text as="span" variant="bodyMd" tone="subdued">
                                                    {oIdx + 1}
                                                </Text>
                                            </div>
                                            <div style={{ flexGrow: 1 }}>
                                                <FormTextField
                                                    name={`steps.${pIdx}.fields.${sIdx}.dropdown_options.${oIdx}`}
                                                    label=""
                                                    labelHidden
                                                    placeholder="Option name"
                                                    value={optionValue}
                                                    error={!optionValue.trim() && "Option cannot be empty"}
                                                    onChange={(val) => {
                                                        const newOptions = [...(field.dropdown_options || [])];
                                                        newOptions[oIdx] = val;
                                                        if (!hasInteracted) setHasInteracted(true);
                                                        handleEditFields(pIdx, sIdx, "dropdown_options", newOptions);
                                                    }}
                                                />
                                            </div>
                                            <div style={{ marginTop: "2px" }}>
                                                <Button
                                                    icon={XIcon}
                                                    variant="tertiary"
                                                    onClick={() => {
                                                        const newOptions = (field.dropdown_options || []).filter(
                                                            (_, i) => i !== oIdx,
                                                        );
                                                        if (!hasInteracted) setHasInteracted(true);
                                                        handleEditFields(pIdx, sIdx, "dropdown_options", newOptions);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                                <div
                                    style={{
                                        border: "1px dashed #c1c1c1",
                                        borderRadius: "8px",
                                        display: "flex",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        padding: "10px",
                                        cursor: "pointer",
                                        marginTop: "4px",
                                        backgroundColor: "#f9fafb",
                                    }}
                                    onClick={() => {
                                        const nextIndex = (field.dropdown_options?.length || 0) + 1;
                                        const defaultValue = `Option ${nextIndex}`;
                                        const newOptions = [...(field.dropdown_options || []), defaultValue];
                                        if (!hasInteracted) setHasInteracted(true);
                                        handleEditFields(pIdx, sIdx, "dropdown_options", newOptions);
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                        <Icon source={PlusCircleIcon} tone="base" />
                                        <Text as="span" variant="bodyMd" fontWeight="medium">
                                            Add Option
                                        </Text>
                                    </div>
                                </div>
                            </BlockStack>
                        </div>

                        {field.dropdown_options && field.dropdown_options.length > 0 && (
                            <div style={{ maxWidth: "50%" }}>
                                <FormSelect
                                    name={`steps.${pIdx}.fields.${sIdx}.default_value`}
                                    label="Default Selection"
                                    options={[
                                        { label: "No default selected", value: "" },
                                        ...field.dropdown_options
                                            .filter((opt) =>
                                                typeof opt === "string"
                                                    ? opt.trim()
                                                    : (opt as any).label || (opt as any).value,
                                            )
                                            .map((opt) => ({
                                                label:
                                                    typeof opt === "string"
                                                        ? opt
                                                        : (opt as any).label || (opt as any).value,
                                                value:
                                                    typeof opt === "string"
                                                        ? opt
                                                        : (opt as any).value || (opt as any).label,
                                            })),
                                    ]}
                                    value={field.default_value || ""}
                                    onChange={(val) => {
                                        if (!hasInteracted) setHasInteracted(true);
                                        handleEditFields(pIdx, sIdx, "default_value", val);
                                    }}
                                />
                            </div>
                        )}
                        {dropdownError && <ShopifyNativeError error={dropdownError} style={{ marginTop: 4 }} />}
                    </BlockStack>
                </div>
            )}
            {field.field_type === "file_upload" && (
                <div style={{ marginTop: "16px" }}>
                    <BlockStack gap="400">
                        <Text as="h3" fontWeight="medium">
                            File Upload Settings
                        </Text>
                        <div
                            style={{
                                border: "1px solid #e1e1e1",
                                borderRadius: "8px",
                                padding: "16px",
                                backgroundColor: "#f9fafb",
                            }}
                        >
                            <BlockStack gap="400">
                                <div className={styles.formRow}>
                                    <FormSelect
                                        name={`steps.${pIdx}.fields.${sIdx}.file_upload_settings.max_file_size`}
                                        label="Max File Size (MB)"
                                        options={[
                                            { label: "1 MB", value: "1" },
                                            { label: "2 MB", value: "2" },
                                            { label: "5 MB", value: "5" },
                                            { label: "10 MB", value: "10" },
                                        ]}
                                        value={String(field.file_upload_settings?.max_file_size || 5)}
                                        onChange={(val) => {
                                            handleEditFields(pIdx, sIdx, "file_upload_settings", {
                                                ...(field.file_upload_settings || {
                                                    max_file_size: 5,
                                                    allow_multiple: false,
                                                    allowed_file_types: ["images"],
                                                }),
                                                max_file_size: Number(val),
                                            });
                                        }}
                                    />
                                    {/* <div style={{ marginTop: "25px" }}>
                                        <Checkbox
                                            label="Allow Multiple Files"
                                            checked={field.file_upload_settings?.allow_multiple || false}
                                            onChange={(val) => {
                                                handleEditFields(pIdx, sIdx, "file_upload_settings", {
                                                    ...(field.file_upload_settings || {
                                                        max_file_size: 5,
                                                        allow_multiple: false,
                                                        allowed_file_types: ["images"],
                                                    }),
                                                    allow_multiple: val,
                                                });
                                            }}
                                        />
                                    </div> */}
                                </div>

                                <BlockStack gap="200">
                                    <Text as="p" variant="bodyMd" fontWeight="medium">
                                        Allowed File Types
                                    </Text>
                                    <div style={{ display: "flex", gap: "16px" }}>
                                        <div
                                            style={{
                                                flex: 1,
                                                padding: "12px",
                                                border: "1px solid #e1e1e1",
                                                borderRadius: "8px",
                                                backgroundColor:
                                                    field.file_upload_settings?.allowed_file_types?.includes("images")
                                                        ? "#f0f7ff"
                                                        : "#ffffff",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}
                                            onClick={() => {
                                                const currentTypes = field.file_upload_settings?.allowed_file_types || [
                                                    "images",
                                                ];
                                                const newTypes = currentTypes.includes("images")
                                                    ? currentTypes.filter((t) => t !== "images")
                                                    : [...currentTypes, "images"];
                                                if (!hasInteracted) setHasInteracted(true);
                                                handleEditFields(pIdx, sIdx, "file_upload_settings", {
                                                    ...(field.file_upload_settings || {
                                                        max_file_size: 5,
                                                        allow_multiple: false,
                                                        allowed_file_types: ["images"],
                                                    }),
                                                    allowed_file_types: newTypes,
                                                });
                                            }}
                                        >
                                            <Checkbox
                                                label=""
                                                checked={field.file_upload_settings?.allowed_file_types?.includes(
                                                    "images",
                                                )}
                                            />
                                            <div>
                                                <Text as="p" fontWeight="medium">
                                                    Images
                                                </Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    JPG, PNG, GIF, SVG
                                                </Text>
                                            </div>
                                        </div>
                                        <div
                                            style={{
                                                flex: 1,
                                                padding: "12px",
                                                border: "1px solid #e1e1e1",
                                                borderRadius: "8px",
                                                backgroundColor:
                                                    field.file_upload_settings?.allowed_file_types?.includes(
                                                        "documents",
                                                    )
                                                        ? "#f0f7ff"
                                                        : "#ffffff",
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "12px",
                                            }}
                                            onClick={() => {
                                                const currentTypes = field.file_upload_settings?.allowed_file_types || [
                                                    "images",
                                                ];
                                                const newTypes = currentTypes.includes("documents")
                                                    ? currentTypes.filter((t) => t !== "documents")
                                                    : [...currentTypes, "documents"];
                                                if (!hasInteracted) setHasInteracted(true);
                                                handleEditFields(pIdx, sIdx, "file_upload_settings", {
                                                    ...(field.file_upload_settings || {
                                                        max_file_size: 5,
                                                        allow_multiple: false,
                                                        allowed_file_types: ["images"],
                                                    }),
                                                    allowed_file_types: newTypes,
                                                });
                                            }}
                                        >
                                            <Checkbox
                                                label=""
                                                checked={field.file_upload_settings?.allowed_file_types?.includes(
                                                    "documents",
                                                )}
                                            />
                                            <div>
                                                <Text as="p" fontWeight="medium">
                                                    Documents
                                                </Text>
                                                <Text as="p" variant="bodySm" tone="subdued">
                                                    PDF, DOC, DOCX
                                                </Text>
                                            </div>
                                        </div>
                                    </div>
                                </BlockStack>
                                {fileUploadError && (
                                    <ShopifyNativeError error={fileUploadError} style={{ marginTop: 8 }} />
                                )}
                            </BlockStack>
                        </div>
                    </BlockStack>
                </div>
            )}
        </Card>
    );
};

const StepsFieldSection = ({
    step,
    index,
    handleEditFields,
    handleAddFields,
    hasInteracted,
    setHasInteracted,
}: {
    step: StepsType;
    index: number;
    handleEditFields: any;
    handleAddFields: any;
    submit: any;
    hasInteracted: boolean;
    setHasInteracted: (val: boolean) => void;
}) => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                gap: 10,
                backgroundColor: "#f5f5f5",
                padding: 16,
                borderRadius: 5,
                border: "1px solid #c1c1c1",
            }}
        >
            {!!step?.fields?.length &&
                step.fields.map((field: any, sIdx: number) => (
                    <FieldRow
                        key={sIdx}
                        handleEditFields={handleEditFields}
                        field={field}
                        sIdx={sIdx}
                        pIdx={index}
                        handleAddFields={handleAddFields}
                        hasInteracted={hasInteracted}
                        setHasInteracted={setHasInteracted}
                    />
                ))}
            {(step?.fields?.length ?? 0) <= 4 && (
                <Button onClick={() => handleAddFields(index, "add")} fullWidth>
                    Add Field
                </Button>
            )}
        </div>
    );
};

const BBStep = ({
    step,
    snapshot,
    index,
    handleEditFields = () => {},
    handleEditStep = () => {},
    handleAddFields = () => {},
    handleDropZoneDrop = () => {},
    isMobile = false,
    submit = () => {},
    setExpandStep,
    expandStep,
    addedNow,
    money_format,
    provided,
}: {
    step: StepsType;
    snapshot: any;
    index: number;
    handleEditFields: any;
    handleEditStep: (idx: number, key: any, value?: any) => void;
    handleAddFields: any;
    handleDropZoneDrop: any;
    isMobile: boolean;
    submit?: () => void;
    setExpandStep: Dispatch<SetStateAction<number>>;
    expandStep: number;
    addedNow: boolean;
    money_format: string;
    provided: any;
}) => {
    const [expandSubStep, setExpandSubStep] = useState(0);
    const [hasInteracted, setHasInteracted] = useState(false);
    const [stepErrors, setStepErrors] = useState<{
        step_label: string;
        product_count_limit: string;
    }>({
        step_label: "",
        product_count_limit: "",
    });

    const { formState } = useFormContext();
    const stepFieldErrors = (formState.errors as any)?.steps?.[index] as Record<string, any> | undefined;
    const hasStepFieldError = !!stepFieldErrors;

    useEffect(() => {
        if (hasInteracted) {
            setStepErrors((ps) => ({
                ...ps,
                step_label: nonEmptyOverride(step?.step_label, "Step Label is required"),
            }));
        } else {
            setStepErrors((ps) => ({ ...ps, step_label: "", product_count_limit: "" }));
        }
    }, [hasInteracted]);

    useEffect(() => {
        if (hasStepFieldError) setHasInteracted(true);
    }, [hasStepFieldError, index]);

    const handleEditSubStep = <K extends keyof SubStepType>(subIdx: number, key: K | "delete", value?: any) => {
        const updatedSubSteps: SubStepType[] = [...step.subSteps];
        if (key === "delete") {
            updatedSubSteps.splice(subIdx, 1);
        } else {
            const current = updatedSubSteps[subIdx];
            let step_error_msg = current.step_error_msg;
            if (key == "subStep_condition_basis") {
                step_error_msg = getConditionMessage(value, current.subStep_condition_type, true);
            }
            if (key == "subStep_condition_type") {
                step_error_msg = getConditionMessage(current.subStep_condition_basis, value, true);
            }
            updatedSubSteps[subIdx] = { ...current, step_error_msg, [key]: value };
        }
        handleEditStep(index, "subSteps", updatedSubSteps);
    };

    const handleAddSubStep = () => {
        const totalSubSteps = step?.subSteps?.length || 0;
        const updatedSubSteps: SubStepType[] =
            step?.subSteps?.length > 0
                ? [...step.subSteps, { ...DEFAULT_SUB_STEP, step_label: `Category ${totalSubSteps + 1}` }]
                : [{ ...DEFAULT_SUB_STEP, step_label: `Category ${totalSubSteps + 1}` }];

        handleEditStep(index, "subSteps", updatedSubSteps);
        setExpandSubStep(totalSubSteps);
    };

    if (!step) return null;

    return (
        <Card
            padding="300"
            background={snapshot.isDragging || (expandStep == index && addedNow) ? "bg-surface-active" : "bg-surface"}
        >
            <div
                onClick={() => setExpandStep((ps) => (ps == index ? -1 : index))}
                className={styles.customCardLabelWrapper}
                style={{ marginBottom: "5px", display: "flex", justifyContent: "space-between", alignItems: "center" }}
            >
                <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", gap: 5 }}>
                    <Icon source={SettingsFilledIcon} />
                    <Label id="review_step">{`Step ${index + 1}`}</Label>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            transform: expandStep == index ? "rotate(90deg)" : "rotate(270deg)",
                        }}
                    >
                        <MdOutlineArrowBackIosNew size={14} cursor="pointer" />
                    </div>
                </div>
                <div className={styles.stepDragIconWrapper}>
                    <Tooltip content="Duplicate">
                        <span
                            className={styles.stepDragIcon}
                            style={{ color: "#9b9b9b", cursor: "pointer" }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleEditStep(index, "copy");
                            }}
                        >
                            <Icon source={DuplicateIcon} />
                        </span>
                    </Tooltip>

                    <span {...provided.dragHandleProps} className={styles.stepDragIcon}>
                        <Icon source={DragHandleIcon} tone={snapshot.isDragging ? "textPrimary" : "base"} />
                    </span>
                    <Tooltip content="Delete">
                        <span
                            className={styles.stepDragIcon}
                            style={{ color: "#9b9b9b" }}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (index > 0) handleEditStep(index, "delete");
                            }}
                        >
                            <Icon source={DeleteIcon} tone={index == 0 ? "inherit" : "critical"} />
                        </span>
                    </Tooltip>
                </div>
            </div>
            <Collapsible
                open={expandStep == index}
                transition={{ duration: "500ms", timingFunction: "ease-in-out" }}
                expandOnPrint
                id=""
            >
                <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
                    <div className={styles.bbStepLabels}>
                        <Text as="h3" fontWeight="medium">
                            1. Step Behaviour
                        </Text>
                        <Text as="h6" tone="subdued">
                            How do you want this step to behave?
                        </Text>
                    </div>

                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: isMobile ? "repeat(1, 1fr)" : "repeat(3, 1fr)",
                            gap: 10,
                        }}
                    >
                        <div
                            className={`${styles.stepBehaviourBox} ${step.step_content_type === "products" ? styles.selectedBox : ""}`}
                            onClick={() => handleEditStep(index, "step_content_type", "products")}
                        >
                            {step.step_content_type === "products" && <span className={styles.activeBox}></span>}
                            <span
                                className={step.step_content_type === "products" ? styles.selectedIcon : styles.boxIcon}
                            >
                                <Icon source={SandboxIcon} tone="base" />
                            </span>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Standard Step
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Customers pick items to build their bundle.
                                </Text>
                            </div>
                        </div>
                        <div
                            className={`${styles.stepBehaviourBox} ${step.step_content_type === "free_gift" ? styles.selectedBox : ""}`}
                            onClick={() => handleEditStep(index, "step_content_type", "free_gift")}
                        >
                            {step.step_content_type === "free_gift" && <span className={styles.activeBox}></span>}
                            <span
                                className={
                                    step.step_content_type === "free_gift" ? styles.selectedIcon : styles.boxIcon
                                }
                            >
                                <Icon source={GiftCardIcon} tone="base" />
                            </span>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Free Gift Step
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Customers select a free reward.
                                </Text>
                            </div>
                        </div>
                        <div
                            className={`${styles.stepBehaviourBox} ${step.step_content_type === "fields" ? styles.selectedBox : ""}`}
                            onClick={() => handleEditStep(index, "step_content_type", "fields")}
                        >
                            {step.step_content_type === "fields" && <span className={styles.activeBox}></span>}
                            <span
                                className={step.step_content_type === "fields" ? styles.selectedIcon : styles.boxIcon}
                            >
                                <Icon source={FileIcon} tone="base" />
                            </span>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Custom field step
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Collect notes or text.
                                </Text>
                            </div>
                        </div>
                    </div>

                    <div className={styles.bbStepLabels}>
                        <Text as="h3" fontWeight="medium">
                            2. {step.step_content_type === "fields" ? "Fields" : "Product"} Configuration
                        </Text>
                    </div>

                    {step.step_content_type === "products" || step.step_content_type === "free_gift" ? (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 16,
                                backgroundColor: "#f5f5f5",
                                padding: 16,
                                borderRadius: 5,
                                border: "1px solid #c1c1c1",
                            }}
                        >
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    Organization
                                </Text>
                                <Text as="h6" tone="subdued">
                                    Choose how products are organized in this step.
                                </Text>
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 10 }}>
                                <div
                                    className={`${styles.productSelectionTypeBox} ${step.enable_subSteps ? "" : styles.selectedBox}`}
                                    onClick={() => handleEditStep(index, "enable_subSteps", false)}
                                >
                                    {!step.enable_subSteps && <span className={styles.activeBox}></span>}
                                    <span className={step.enable_subSteps ? styles.boxIcon : styles.selectedIcon}>
                                        <Icon source={ListBulletedIcon} tone="base" />
                                    </span>
                                    <div className={styles.bbStepLabels}>
                                        <Text as="h3" fontWeight="medium">
                                            Single List
                                        </Text>
                                    </div>
                                </div>
                                <div
                                    className={`${styles.productSelectionTypeBox} ${step.enable_subSteps ? styles.selectedBox : ""}`}
                                    onClick={() => handleEditStep(index, "enable_subSteps", true)}
                                >
                                    {step.enable_subSteps && <span className={styles.activeBox}></span>}
                                    <span className={step.enable_subSteps ? styles.selectedIcon : styles.boxIcon}>
                                        <Icon source={AppsIcon} tone="base" />
                                    </span>
                                    <div className={styles.bbStepLabels}>
                                        <Text as="h3" fontWeight="medium">
                                            Categories
                                        </Text>
                                    </div>
                                </div>
                            </div>

                            {step?.enable_subSteps ? (
                                <div style={{ border: "1px solid #ddd", borderRadius: "10px" }}>
                                    {!step.subSteps || step.subSteps.length === 0 ? (
                                        <div
                                            style={{
                                                backgroundColor: "#f9fafb",
                                                borderRadius: "10px",
                                                padding: "32px 24px",
                                                textAlign: "center",
                                            }}
                                        >
                                            <p style={{ margin: "0 0 8px 0", fontWeight: "600", color: "#374151" }}>
                                                No categories added yet
                                            </p>
                                            <p style={{ margin: "0 0 16px 0", color: "#6b7280", fontSize: "14px" }}>
                                                Create categories to group products like Base, Add-ons, or Extras.
                                            </p>
                                            <Button onClick={() => handleAddSubStep()} icon={PlusCircleIcon}>
                                                Add Category
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className={styles.subStepsWrapper}>
                                            {step?.subSteps?.length > 0 &&
                                                step?.subSteps?.map((subStep, cIdx) => (
                                                    <div key={cIdx}>
                                                        <BBSubStep
                                                            subStep={subStep}
                                                            cIdx={cIdx}
                                                            pIdx={index}
                                                            submit={submit}
                                                            handleEditSubStep={handleEditSubStep}
                                                            setExpandSubStep={setExpandSubStep}
                                                            expandSubStep={expandSubStep}
                                                            money_format={money_format}
                                                        />
                                                    </div>
                                                ))}
                                            {step?.subSteps?.length <= 10 && (
                                                <div style={{ marginTop: 10, padding: "0 16px 16px 16px" }}>
                                                    <Button
                                                        onClick={() => handleAddSubStep()}
                                                        fullWidth
                                                        icon={PlusCircleIcon}
                                                    >
                                                        Add Category
                                                    </Button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={`${styles.formRow} ${styles.row2}`}>
                                    <div>
                                        <div className={styles.tabsContainer}>
                                            <Tabs
                                                tabs={[
                                                    {
                                                        id: "product_selection_specific",
                                                        content: "Specific Products",
                                                        panelID: "product_selection_specific",
                                                    },
                                                    {
                                                        id: "product_selection_collection",
                                                        content: "Collections",
                                                        panelID: "product_selection_collection",
                                                    },
                                                ]}
                                                selected={step.product_selection == "specific" ? 0 : 1}
                                                onSelect={(idx) =>
                                                    handleEditStep(
                                                        index,
                                                        "product_selection",
                                                        idx == 0 ? "specific" : "collection",
                                                    )
                                                }
                                            ></Tabs>
                                        </div>

                                        {step.product_selection == "specific" && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    marginTop: 10,
                                                    marginBottom: 10,
                                                }}
                                            >
                                                <RHFRenderSelectedProduct
                                                    bundleLevel="product"
                                                    bundleType={BundleTypes.BUNDLE_BUILDER}
                                                    resourcePickerQuery={buildResourcePickerQuery({
                                                        includeUnlisted: true,
                                                    })}
                                                    updateKey={`steps.${index}.products`}
                                                    multiple={true}
                                                    value={step.products}
                                                    errorMessage={stepFieldErrors?.products?.message}
                                                    onChange={(value: ProductObj[]) => {
                                                        handleEditStep(index, "products", value);
                                                        submit();
                                                    }}
                                                />
                                            </div>
                                        )}

                                        {step.product_selection == "collection" && (
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    marginTop: 10,
                                                    marginBottom: 10,
                                                }}
                                            >
                                                <RHFRenderSelectedCollections
                                                    updateKey={`steps.${index}.collections`}
                                                    multiple={false}
                                                    value={step.collections}
                                                    errorMessage={stepFieldErrors?.collections?.message}
                                                    onChange={(value: ProductObj[]) => {
                                                        handleEditStep(index, "collections", value);
                                                        submit();
                                                    }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : step.step_content_type === "fields" ? (
                        <StepsFieldSection
                            key={"field" + index}
                            step={step}
                            index={index}
                            handleEditFields={handleEditFields}
                            handleAddFields={handleAddFields}
                            submit={submit}
                            hasInteracted={hasInteracted}
                            setHasInteracted={setHasInteracted}
                        />
                    ) : null}

                    {step.step_content_type != "fields" && (
                        <>
                            <div className={styles.bbStepLabels}>
                                <Text as="h3" fontWeight="medium">
                                    3. Rules and Limits
                                </Text>
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 16,
                                    backgroundColor: "#f5f5f5",
                                    padding: 16,
                                    borderRadius: 5,
                                    border: "1px solid #c1c1c1",
                                }}
                            >
                                <div
                                    className={`${styles.formRow} ${styles.row6}`}
                                    style={{ display: "flex", flexDirection: "column", gap: 10 }}
                                >
                                    <FormCheckbox
                                        name={`steps.${index}.render_as_variants`}
                                        label="Show Variants as Individual Products"
                                        checked={step.render_as_variants}
                                        onChange={(val) => handleEditStep(index, "render_as_variants", val)}
                                    />
                                    {isLilSweetTreatStore && !step.enable_subSteps && (
                                        <>
                                            <FormCheckbox
                                                name={`steps.${index}.allow_product_custom_text`}
                                                label="Allow Customers to add Personalized Text for products"
                                                checked={step.allow_product_custom_text}
                                                onChange={(val) =>
                                                    handleEditStep(index, "allow_product_custom_text", val)
                                                }
                                            />
                                            {step.allow_product_custom_text && (
                                                <div
                                                    style={{
                                                        paddingLeft: "25px",
                                                        maxWidth: "400px",
                                                        display: "flex",
                                                        flexDirection: "column",
                                                        gap: 10,
                                                    }}
                                                >
                                                    <FormTextField
                                                        name={`steps.${index}.product_custom_text_label`}
                                                        label="Custom Text Label"
                                                        value={step.product_custom_text_label}
                                                        onChange={(val) =>
                                                            handleEditStep(index, "product_custom_text_label", val)
                                                        }
                                                        placeholder="e.g. Add your name"
                                                    />
                                                    <FormTextField
                                                        name={`steps.${index}.product_custom_text_placeholder`}
                                                        label="Custom Text Placeholder"
                                                        value={step.product_custom_text_placeholder}
                                                        onChange={(val) =>
                                                            handleEditStep(
                                                                index,
                                                                "product_custom_text_placeholder",
                                                                val,
                                                            )
                                                        }
                                                        placeholder="e.g. Enter your name here"
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                    {isDispensaryCoStore && (
                                        <>
                                            <FormCheckbox
                                                name={`steps.${index}.enable_dosage_calculation`}
                                                label="Enable Dosage Calculation"
                                                checked={step.enable_dosage_calculation}
                                                onChange={(val) =>
                                                    handleEditStep(index, "enable_dosage_calculation", val)
                                                }
                                            />
                                            <FormCheckbox
                                                name={`steps.${index}.enable_capacity_progress_bar`}
                                                label="Enable Capacity Progress Bar"
                                                checked={step.enable_capacity_progress_bar}
                                                onChange={(val) =>
                                                    handleEditStep(index, "enable_capacity_progress_bar", val)
                                                }
                                            />
                                            {step.enable_capacity_progress_bar && (
                                                <div style={{ paddingLeft: 24 }}>
                                                    <FormCheckbox
                                                        name={`steps.${index}.merge_total_capacity`}
                                                        label="Merge Total Capacity"
                                                        helpText={
                                                            step.merge_total_capacity
                                                                ? "Sum all selected products' capacities into one progress bar."
                                                                : "Show individual capacity icons sorted by each product's capacity value."
                                                        }
                                                        checked={step.merge_total_capacity ?? true}
                                                        onChange={(val) =>
                                                            handleEditStep(index, "merge_total_capacity", val)
                                                        }
                                                    />
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>

                                <Divider />

                                {step?.enable_subSteps ? (
                                    <>
                                        <p style={{ margin: 0, color: "#303030", fontSize: "13px" }}>
                                            Conditions for this step
                                        </p>
                                        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                                            <FormRadioButton
                                                label="No Rules"
                                                name={`steps.${index}.substep_rules_type`}
                                                value="no_rules"
                                                onChange={() =>
                                                    handleEditStep(index, {
                                                        substep_rules_type: "no_rules",
                                                        show_step_conditions: false,
                                                    })
                                                }
                                            />
                                            <FormRadioButton
                                                label="Step Level Rules"
                                                name={`steps.${index}.substep_rules_type`}
                                                value="step_level"
                                                onChange={() =>
                                                    handleEditStep(index, {
                                                        substep_rules_type: "step_level",
                                                        show_step_conditions: true,
                                                    })
                                                }
                                            />
                                            <FormRadioButton
                                                label="Category Level Rules"
                                                name={`steps.${index}.substep_rules_type`}
                                                value="substep_level"
                                                onChange={() =>
                                                    handleEditStep(index, {
                                                        substep_rules_type: "substep_level",
                                                        show_step_conditions: false,
                                                    })
                                                }
                                            />
                                        </div>

                                        {step?.substep_rules_type === "step_level" && (
                                            <StepConditions
                                                step={step}
                                                handleEditStep={handleEditStep}
                                                stepIdx={index}
                                                styles={styles}
                                                bundle_type={BundleTypes.BUNDLE_BUILDER}
                                            />
                                        )}

                                        {step?.substep_rules_type === "substep_level" && (
                                            <>
                                                {step?.subSteps?.length == 0 ? (
                                                    <LegacyCard title="">
                                                        <Banner tone="info">
                                                            Add a category to adjust category level rules.
                                                        </Banner>
                                                    </LegacyCard>
                                                ) : (
                                                    <div style={{ border: "1px solid #ddd", borderRadius: 10 }}>
                                                        {step?.subSteps?.map((subStep: any, cIdx: number) => (
                                                            <div key={"substep_" + cIdx}>
                                                                <BBSubStepConditions
                                                                    subStep={subStep}
                                                                    cIdx={cIdx}
                                                                    pIdx={index}
                                                                    isMobile={isMobile}
                                                                    handleEditSubStep={handleEditSubStep}
                                                                    money_format={money_format}
                                                                    substep_rules_type={step?.substep_rules_type}
                                                                />
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <p style={{ margin: 0, color: "#303030", fontSize: "13px" }}>
                                            Conditions for this step
                                        </p>

                                        <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
                                            <FormRadioButton
                                                label="No Rules"
                                                name={`steps.${index}.show_step_conditions`}
                                                value="false"
                                                checked={step?.show_step_conditions === false}
                                                onChange={() => handleEditStep(index, "show_step_conditions", false)}
                                            />
                                            <FormRadioButton
                                                label="Step Level Rules"
                                                name={`steps.${index}.show_step_conditions`}
                                                value="true"
                                                checked={step?.show_step_conditions === true}
                                                onChange={() => handleEditStep(index, "show_step_conditions", true)}
                                            />
                                        </div>

                                        {step?.show_step_conditions && (
                                            <StepConditions
                                                step={step}
                                                handleEditStep={handleEditStep}
                                                stepIdx={index}
                                                styles={styles}
                                                bundle_type={BundleTypes.BUNDLE_BUILDER}
                                            />
                                        )}
                                    </>
                                )}
                            </div>
                        </>
                    )}
                    <div className={styles.bbStepLabels}>
                        <Text as="h3" fontWeight="medium">
                            {step.step_content_type === "fields" ? "3" : "4"}. Step Info
                        </Text>
                    </div>

                    <div
                        style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 16,
                            backgroundColor: "#f5f5f5",
                            padding: 16,
                            borderRadius: 5,
                            border: "1px solid #c1c1c1",
                        }}
                    >
                        <div
                            className={`${styles.formRow} ${styles.row4}`}
                            style={{
                                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                                gap: isMobile ? "8px" : "16px",
                            }}
                        >
                            <FormTextField
                                name={`steps.${index}.step_label`}
                                label="Step Label"
                                placeholder="Step Label"
                                value={step.step_label}
                                onBlur={() => {
                                    if (!hasInteracted) setHasInteracted(true);
                                }}
                                onChange={(val) => {
                                    setStepErrors((ps) => ({
                                        ...ps,
                                        step_label: nonEmptyOverride(val, "Step Label is required"),
                                    }));
                                    if (!hasInteracted) setHasInteracted(true);
                                    handleEditStep(index, "step_label", val);
                                }}
                                maxLength={20}
                                showCharacterCount
                                error={stepErrors?.step_label}
                            />
                            <FormTextField
                                name={`steps.${index}.page_title`}
                                label="Page Title (optional)"
                                placeholder="Page Title"
                                value={step.page_title}
                                onChange={(val) => handleEditStep(index, "page_title", val)}
                                maxLength={80}
                                showCharacterCount
                            />
                        </div>
                        <div className={`${styles.formRow} ${styles.row4}`} style={{ gridTemplateColumns: "1fr" }}>
                            <FormTextField
                                name={`steps.${index}.page_subtitle`}
                                label="Page Subtitle (optional)"
                                placeholder="Page Subtitle"
                                value={step.page_subtitle}
                                onChange={(val) => handleEditStep(index, "page_subtitle", val)}
                                maxLength={200}
                                showCharacterCount
                            />
                        </div>
                        {[
                            "quickstart-f02489ba.myshopify.com",
                            MAYUR_TEST_STORE,
                            "keutek.myshopify.com",
                            "test-bundle-29.myshopify.com",
                            "ifxrkd-zb.myshopify.com",
                        ].includes(shopify?.config?.shop || "") && (
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <Label id="bottom_subtitle">Header Description</Label>
                                <MonacoEditor
                                    className={styles.monacoEditor}
                                    height="25vh"
                                    value={step?.header_subtitle || ""}
                                    defaultLanguage="html"
                                    defaultValue=""
                                    onChange={(value: any) => handleEditStep(index, "header_subtitle", value)}
                                />

                                <Label id="bottom_subtitle">Bottom Description</Label>
                                <MonacoEditor
                                    className={styles.monacoEditor}
                                    height="25vh"
                                    value={step?.bottom_subtitle || ""}
                                    defaultLanguage="html"
                                    defaultValue=""
                                    onChange={(value: any) => handleEditStep(index, "bottom_subtitle", value)}
                                />
                            </div>
                        )}
                        <div>
                            <span>
                                <Label id="widget_icon">
                                    <span
                                        style={{
                                            display: "flex",
                                            justifyContent: "flex-start",
                                            alignItems: "center",
                                            gap: 5,
                                        }}
                                    >
                                        Add Widget Icon
                                        <Tooltip
                                            width="wide"
                                            content="Upload image: Accepts .jpg, .png, .svg and .gif files up to 5MB. Image dimensions should not be greater than 512x512 pixels."
                                        >
                                            <Icon source={InfoIcon} />
                                        </Tooltip>
                                    </span>
                                </Label>
                            </span>
                            <div style={{ marginTop: 4, display: "flex", alignItems: "center" }}>
                                {/* Show the upper DropZone only if there are no files */}
                                {step?.step_icon == null ? (
                                    <div style={{ width: 40, height: 40 }}>
                                        <DropZone onDrop={handleDropZoneDrop}>
                                            <DropZone.FileUpload
                                                actionTitle="Click to upload or drag and drop"
                                                actionHint=""
                                            />
                                        </DropZone>
                                    </div>
                                ) : (
                                    <div
                                        className={styles.uploadedImage}
                                        style={{
                                            height: "50px",
                                            minWidth: step?.step_icon?.originalSrc ? "50px" : "100%",
                                            width: "min-content",
                                        }}
                                    >
                                        {step?.step_icon?.originalSrc ? (
                                            <img
                                                src={step?.step_icon?.originalSrc}
                                                alt={step?.step_icon.alt}
                                                style={{ objectFit: "contain", width: "auto", height: "100%" }}
                                            />
                                        ) : (
                                            <div className={styles.uploadingPlaceholder} style={{ width: "100%" }}>
                                                Uploading...
                                            </div>
                                        )}

                                        <button
                                            className={styles.deleteButton}
                                            onClick={() => handleEditStep(index, "step_icon", null)}
                                        >
                                            <Icon source={DeleteIcon} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className={`${styles.formRow} ${styles.row4}`} style={{ gridTemplateColumns: "1fr 1fr" }}>
                            <FormCheckbox
                                name={`steps.${index}.show_banner`}
                                label="Add Page Banner"
                                checked={step?.show_banner}
                                onChange={(val) => handleEditStep(index, "show_banner", val)}
                            />
                        </div>

                        {step?.show_banner && (
                            <div
                                className={`${styles.formRow} ${styles.row4}`}
                                style={{ gridTemplateColumns: "1fr 1fr" }}
                            >
                                <ImageUploader
                                    handleInputChange={handleEditStep}
                                    img_url={step?.banner_desktop || ""}
                                    selectedTab={index}
                                    keyName="banner_desktop"
                                    heading="Banner Image: Desktop"
                                    helpMessage="Recommended Size: 1900x230"
                                />
                                <ImageUploader
                                    handleInputChange={handleEditStep}
                                    img_url={step?.banner_mobile || ""}
                                    selectedTab={index}
                                    keyName="banner_mobile"
                                    heading="Banner Image: Mobile"
                                    helpMessage="Recommended Size: 1100x500"
                                />
                            </div>
                        )}
                    </div>
                </div>
            </Collapsible>
        </Card>
    );
};

export default BBStep;
